import * as Popover from "@radix-ui/react-popover";
import clsx from "clsx";
import { ChevronDown, TableProperties } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

/** Toggle pill for a table column: highlighted when shown, dashed when unavailable. */
export const displayPillClass = (shown: boolean, disabled = false) =>
  clsx(
    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-info",
    disabled
      ? "cursor-not-allowed border-dashed border-outline text-text-dim"
      : shown
        ? "cursor-pointer border-info/40 bg-info/10 text-info"
        : "cursor-pointer border-outline text-text-mid hover:bg-muted-surface hover:text-text",
  );

/** A directory's layout popover (ordering, visible columns, ...). Changes apply live. */
export default function DisplayMenu({
  modified,
  onReset,
  children,
}: {
  modified: boolean;
  onReset: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        className={clsx("dropdown-trigger shrink-0", open && "filter-active")}
      >
        <TableProperties aria-hidden="true" className="h-4 w-4" />
        {t("process_directory.display")}
        {modified && (
          <>
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-info" />
            <span className="sr-only">{t("process_directory.display_customized")}</span>
          </>
        )}
        <ChevronDown aria-hidden="true" className="h-4 w-4 text-text-dim" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          aria-label={t("process_directory.display")}
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className="dropdown-menu w-80 max-w-[calc(100vw-32px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3"
        >
          <div className="mb-3 flex min-h-7 items-center justify-between gap-3">
            <span className="text-sm font-medium text-text">{t("process_directory.display")}</span>
            {modified && (
              <button
                type="button"
                onClick={onReset}
                className="rounded-md px-1 py-0.5 text-xs font-medium text-info hover:underline focus-visible:outline-info"
              >
                {t("process_directory.reset_display")}
              </button>
            )}
          </div>
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
